/**
 * Màn khách xem ẢNH IN.
 *
 * Khách mở trên điện thoại, giữa lúc bận, và không có tài khoản nào để đăng
 * nhập. Nên: chữ to, một cột trên máy nhỏ, và mọi lỗi nói bằng câu người thường
 * đọc được kèm việc cần làm tiếp — không mã lỗi, không thuật ngữ.
 *
 * Trang RIÊNG chứ không thêm một chế độ nữa vào `CustomerAlbumPage`: trang kia
 * đã mang ba chế độ với ternary lồng nhau ở `queryKey`/`queryFn`, thêm chế độ
 * thứ tư là chỗ dễ sai nhất của cả file. Chữ ở đây cũng khác thật ("ảnh in" là
 * bản đi in, không phải bộ ảnh giao).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PhotoMasonry } from "@/components/photo-grid/PhotoMasonry";
import { Download } from "lucide-react";

import { Button } from "@/ui/button";
import { Skeleton } from "@/ui/skeleton";
import {
  downloadCustomerPrintZip,
  openCustomerPrintAlbum,
  printPhotoKeys,
  type CustomerAlbum,
} from "./api";

export function CustomerPrintAlbumPage({ token }: { token: string }) {
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const albumQ = useQuery({
    queryKey: printPhotoKeys.customer(token),
    queryFn: () => openCustomerPrintAlbum(token),
    enabled: Boolean(token),
    retry: false,
  });

  if (!token) {
    return <Message title="Thiếu link" detail="Chị mở lại đường link studio đã gửi giúp em ạ." />;
  }
  if (albumQ.isLoading) return <AlbumSkeleton />;
  if (albumQ.isError) {
    return <Message title="Không mở được ảnh in" detail={(albumQ.error as Error).message} />;
  }

  const album = albumQ.data as CustomerAlbum;
  if (album.photos.length === 0) {
    return (
      <Message
        title="Ảnh in đang được chuẩn bị"
        detail="Studio sẽ báo chị ngay khi ảnh in sẵn sàng ạ."
      />
    );
  }

  const zipFilename = archiveFilename(album.orderCode);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6">
      <header className="mb-5 space-y-1">
        <h1 className="text-2xl font-semibold md:text-3xl">Ảnh in của {album.customerName}</h1>
        <p className="text-sm text-muted-foreground">
          Đơn {album.orderCode} · {album.photos.length} ảnh · Link xem đến{" "}
          {formatDate(album.expiresAt)}
        </p>
      </header>

      <div className="mb-4">
        <Button
          type="button"
          className="min-h-[48px] w-full sm:w-auto"
          disabled={downloadBusy}
          onClick={async () => {
            setDownloadError(null);
            setDownloadBusy(true);
            try {
              await downloadCustomerPrintZip(token, zipFilename);
            } catch (e) {
              setDownloadError((e as Error).message);
            } finally {
              setDownloadBusy(false);
            }
          }}
        >
          <Download className="mr-1 h-4 w-4" aria-hidden />
          {downloadBusy ? "Đang chuẩn bị…" : `Tải tất cả ${album.photos.length} ảnh`}
        </Button>
        {downloadError && (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {downloadError}
          </p>
        )}
      </div>

      {/* Lưới dùng chung với mười màn kia: ô ôm ĐÚNG KHỔ từng tấm. Ảnh in
          bị ép về 3:4 là khách nhìn thấy một khung khác hẳn khung sẽ in ra. */}
      <PhotoMasonry
        label="Album ảnh in"
        tiles={album.photos.map((p) => ({
          id: p.photoId,
          // Bản vừa cho ô (CEO chốt 1a 30/09/2026); máy chủ cũ chưa trả thì lùi về `url`.
          url: p.gridUrl || p.url,
          width: p.width ?? null,
          height: p.height ?? null,
          flags: [],
          caption: `Ảnh ${p.seq}`,
          label: `Ảnh in ${p.seq}`,
          interactive: false,
          /* Tải về phải là BẢN ĐẦY ĐỦ, không phải ảnh đang hiện: khách lưu
             ảnh in về để mang đi in, bản xem trước in ra là vỡ.

             `download` ở đây gần như trang trí: link ký nằm ở origin của kho
             ảnh, mà thuộc tính này thì trình duyệt bỏ qua khi khác origin.
             Thứ thật sự bắt trình duyệt LƯU thay vì MỞ RA XEM là header
             Content-Disposition máy chủ ký kèm vào `downloadUrl`. */
          overlay: (
            <a
              href={p.downloadUrl}
              download={`anh-in-${p.seq}.jpg`}
              className="absolute bottom-1.5 right-1.5 inline-flex min-h-[44px] items-center gap-1 rounded-full bg-black/55 px-3 text-sm font-medium text-white"
            >
              <Download className="h-4 w-4" aria-hidden />
              Tải
            </a>
          ),
        }))}
        onOpen={() => {}}
        emptyText="Album ảnh in chưa có tấm nào."
      />
    </div>
  );
}

function Message({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="mx-auto w-full max-w-lg px-4 py-16 text-center">
      <h1 className="mb-2 text-xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{detail}</p>
    </div>
  );
}

function AlbumSkeleton() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6">
      <Skeleton className="mb-5 h-9 w-64" />
      {/* Khung chờ so le chứ KHÔNG cùng một khổ: lưới thật ôm đúng khổ từng
          tấm, nên khung chờ vuông vắn đều tăm tắp là hứa một bố cục rồi đổi
          ngay khi ảnh về. */}
      <div className="flex gap-[2px]">
        {[0, 1].map((lan) => (
          <div key={lan} className="flex min-w-0 flex-1 flex-col gap-[2px]">
            {[3 / 4, 4 / 3, 2 / 3, 1].map((tiLe, i) => (
              <Skeleton key={i} className="w-full" style={{ aspectRatio: String(tiLe) }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Tên tệp zip khách nhận. Mang mã đơn để tải nhiều đơn về không lẫn nhau. */
function archiveFilename(orderCode: string): string {
  const safe = orderCode.replace(/[^A-Za-z0-9-]/g, "") || "anh-in";
  return `${safe}-anh-in.zip`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("vi-VN");
}
